import React, { useState } from 'react';
import styled from 'styled-components';
import Layout from '../../components/Layout';
import Card from '../../components/Card';

const PageTitle = styled.h2`
  font-size: 28px;
  font-weight: 600;
  color: #1A2440;
  margin-bottom: 20px;
`;

const ExportButton = styled.button`
  background-color: #1A2440;
  color: white;
  border: none;
  border-radius: 4px;
  padding: 8px 16px;
  font-size: 14px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 20px;
  
  &:hover {
    background-color: #2c3a5a;
  }
`;

const TabsContainer = styled.div`
  display: flex;
  margin-bottom: 20px;
  border-bottom: 1px solid #e9ecef;
`;

const Tab = styled.button<{ active: boolean }>`
  padding: 12px 20px;
  background: none;
  border: none;
  border-bottom: 3px solid ${props => props.active ? '#1A2440' : 'transparent'};
  color: ${props => props.active ? '#1A2440' : '#6c757d'};
  font-size: 16px;
  font-weight: ${props => props.active ? '600' : '400'};
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    color: #1A2440;
  }
`;

const ManagementGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 20px;
  margin-bottom: 30px;
`;

const ExecutiveCard = styled.div`
  background-color: #ffffff;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
`;

const ExecutiveHeader = styled.div`
  padding: 20px;
  background-color: #f8f9fa;
  border-bottom: 1px solid #e9ecef;
`;

const ExecutiveName = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: #1A2440;
  margin: 0 0 5px;
`;

const ExecutiveTitle = styled.div`
  font-size: 14px;
  color: #6c757d;
`;

const ExecutiveContent = styled.div`
  padding: 20px;
`;

const ExecutiveDetail = styled.div`
  margin-bottom: 15px;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const DetailLabel = styled.div`
  font-size: 12px;
  color: #6c757d;
  margin-bottom: 5px;
`;

const DetailValue = styled.div`
  font-size: 14px;
  color: #1A2440;
`;

const RiskTag = styled.span`
  display: inline-block;
  background-color: #ffebee;
  color: #E45858;
  font-size: 12px;
  padding: 4px 8px;
  border-radius: 4px;
  margin-right: 5px;
  margin-top: 5px;
`;

const OrgChartContainer = styled.div`
  background-color: #ffffff;
  border-radius: 8px;
  padding: 20px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
`;

const OrgChartNode = styled.div`
  border: 1px solid #e9ecef;
  border-radius: 4px;
  padding: 10px;
  margin-bottom: 10px;
  background-color: #f8f9fa;
  
  &.ceo {
    background-color: #e3f2fd;
    border-color: #90caf9;
  }
  
  &.executive {
    background-color: #f1f8e9;
    border-color: #c5e1a5;
  }
`;

const OrgChartLevel = styled.div`
  margin-left: 30px;
  position: relative;
  
  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: -15px;
    height: 100%;
    border-left: 1px solid #e9ecef;
  }
`;

// Sample management data
const managementData = [
  {
    id: 1,
    name: "John Smith",
    title: "Chief Executive Officer",
    background: "20+ years in software industry, previously CEO at Tech Solutions Inc.",
    tenure: "5 years",
    education: "MBA, Harvard Business School",
    keyPersonRisk: true,
  },
  {
    id: 2,
    name: "Sarah Johnson",
    title: "Chief Financial Officer",
    background: "15+ years in finance, previously CFO at Global Software Corp.",
    tenure: "3 years",
    education: "CPA, MBA from Stanford",
    keyPersonRisk: false,
  },
  {
    id: 3,
    name: "Michael Chen",
    title: "Chief Technology Officer",
    background: "18+ years in software development, previously VP of Engineering at InnoTech.",
    tenure: "4 years",
    education: "MS Computer Science, MIT",
    keyPersonRisk: true,
  },
  {
    id: 4,
    name: "Emily Rodriguez",
    title: "Chief Marketing Officer",
    background: "12+ years in B2B marketing, previously Marketing Director at SaaS Leaders.",
    tenure: "2 years",
    education: "BA Marketing, Northwestern University",
    keyPersonRisk: false,
  },
];

const ManagementPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState('team');

  return (
    <Layout 
      companyName="Sample Company"
      sector="Software"
      location="United States"
    >
      <PageTitle>Management</PageTitle>
      
      <ExportButton>
        Export Bio Summary PDF
      </ExportButton>
      
      <TabsContainer>
        <Tab 
          active={activeTab === 'team'} 
          onClick={() => setActiveTab('team')}
        >
          Management Team
        </Tab>
        <Tab 
          active={activeTab === 'org'} 
          onClick={() => setActiveTab('org')}
        >
          Org Chart
        </Tab>
      </TabsContainer>
      
      {activeTab === 'team' && (
        <ManagementGrid>
          {managementData.map(executive => (
            <ExecutiveCard key={executive.id}>
              <ExecutiveHeader>
                <ExecutiveName>{executive.name}</ExecutiveName>
                <ExecutiveTitle>{executive.title}</ExecutiveTitle>
              </ExecutiveHeader>
              <ExecutiveContent>
                <ExecutiveDetail>
                  <DetailLabel>Background</DetailLabel>
                  <DetailValue>{executive.background}</DetailValue>
                </ExecutiveDetail>
                <ExecutiveDetail>
                  <DetailLabel>Tenure</DetailLabel>
                  <DetailValue>{executive.tenure}</DetailValue>
                </ExecutiveDetail>
                <ExecutiveDetail>
                  <DetailLabel>Education</DetailLabel>
                  <DetailValue>{executive.education}</DetailValue>
                </ExecutiveDetail>
                {executive.keyPersonRisk && (
                  <RiskTag>Key Person Risk</RiskTag>
                )}
              </ExecutiveContent>
            </ExecutiveCard>
          ))}
        </ManagementGrid>
      )}
      
      {activeTab === 'org' && (
        <OrgChartContainer>
          <OrgChartNode className="ceo">
            John Smith - Chief Executive Officer
          </OrgChartNode>
          <OrgChartLevel>
            <OrgChartNode className="executive">
              Sarah Johnson - Chief Financial Officer
            </OrgChartNode>
            <OrgChartLevel>
              <OrgChartNode>
                Robert Lee - VP of Finance
              </OrgChartNode>
              <OrgChartNode>
                Lisa Wong - Controller
              </OrgChartNode>
            </OrgChartLevel>
            
            <OrgChartNode className="executive">
              Michael Chen - Chief Technology Officer
            </OrgChartNode>
            <OrgChartLevel>
              <OrgChartNode>
                David Kim - VP of Engineering
              </OrgChartNode>
              <OrgChartNode>
                Jennifer Taylor - VP of Product
              </OrgChartNode>
            </OrgChartLevel>
            
            <OrgChartNode className="executive">
              Emily Rodriguez - Chief Marketing Officer
            </OrgChartNode>
            <OrgChartLevel>
              <OrgChartNode>
                James Wilson - Director of Marketing
              </OrgChartNode>
            </OrgChartLevel>
          </OrgChartLevel>
        </OrgChartContainer>
      )}
    </Layout>
  );
};

export default ManagementPage;
